import React, {useContext} from "react";
import {Formik, useFormikContext} from "formik";
import * as Yup from "yup";
import moment from "moment";
import numeral from "numeral";
import {useSelector} from "react-redux";
import {QcPopUpContext} from "./QCPopUp";
import {usePolicyContext} from "../../../../clientServicing/Payments/PolicyContext";
import {useActions} from "../../../../../actions";
import * as PaymentActions from "../../../../../actions/payment";
import {fieldsName, paymentMethodsType} from "./utils";
import FormFields from "./FormFields";
import {RootState} from "../../../../../reducers";
import {isDebug, isFirstPaymentDone} from "../../../../../utils/commonUtils";
import {Frequency, GWTVersionEnum} from "../../../../../constants";
import {ClientSplitAmountType} from "../../../../clientServicing/Payments";
import {OnSubmitFormikType} from "../../../../../model/fields";
import {getPaymentAmount} from "../../../../../model/payment";

export interface QCPopUpContentProps {
    actionPayload?: any,
    client?: any
    children?: React.ReactNode,
}


export default function QCPopUpContent(props: QCPopUpContentProps) {
    const debugMode = isDebug();
    const clientDetail = useSelector((state: RootState) => state.clientAccount);
    const {actionPayload, children} = props
    const {client, enrollmentId, setOpenPopup, refreshList, setSubmitting, clientPolicy} = useContext(QcPopUpContext)
    const clientName = `${client && client.user.firstName} ${client && client.user.lastName}`
    const {policy, refetchPolicy}: any = usePolicyContext();
    const {createFirstPayment, createSinglePayment} = useActions(PaymentActions);
    const paymentFrequency: string = clientPolicy && clientPolicy.paymentFrequency || "annual";
    const currentTranche = clientPolicy && clientPolicy.tranche && clientPolicy.tranche.trancheNumber;
    const initialValues = {[fieldsName.trancheNo]: currentTranche || ""};
    const GWT_VERSION: GWTVersionEnum | undefined = clientDetail && clientDetail.client && clientDetail.client.gwtVersion || undefined;
    const submitFormik: OnSubmitFormikType = async (values, actions) => {
        const submitting = (value: boolean) => {
            setSubmitting && setSubmitting(value)
            actions.setSubmitting(value);
        };

        submitting(true)

        const paymentMethodId = values.paymentMethodId;

        // Check if payment method added
        if (!paymentMethodId) {
            submitting(false);
            return;
        }

        const trancheNoValue = values[fieldsName.trancheNo];

        // returns true if the first payment is done [at least one payment is done for the policy]
        const firstPaymentDone: boolean = isFirstPaymentDone(policy.clientPayments);

        const commonPayload: any = {
            insuredName: clientName,
            callingFrom: "QC-ACTION-ITEMS",
            clientId: client.id,
            // enrollmentId: clientPolicy &&  clientPolicy.enrollment && clientPolicy.enrollment.id,
            enrollmentId: enrollmentId,
            isNewPaymentMethod: false, // always false as !paymentMethodId will now allow to continue here
            payerEmail: client.user.email,
            payerName: clientName,
            payerTrancheNo: trancheNoValue,
            type: paymentFrequency === "monthly" ? "MonthlyEnrollment" : "AnnualEnrollment",
            paymentFrequency: !!paymentFrequency && paymentFrequency === "monthly" ? Frequency.Monthly : Frequency.Annual,
            paymentMethodId,
            policy: {
                carrier: policy.carrier,
                clientPayments: policy.clientPayments,
                policyNumberCarrier: policy.policyNumberCarrier,
                premium: policy.premium,
            },
            policyNumber: policy.policyNumber,
        };
        // payment api call
        if (firstPaymentDone) {
            const merchantPayment = {amount: 3, type: ClientSplitAmountType.Merchant};
            const singlePaymentPayload: any = {
                ...commonPayload,
                clientPayment: !!actionPayload ? actionPayload : {
                    date: moment().format("YYYY-MM-DD"),
                    type: "ePay",
                    details: values.notes,
                    processedBy: clientName,
                    amount: [
                        {amount: numeral(values.paymentAmount).value(), type: ClientSplitAmountType.Premium},
                        merchantPayment
                    ],
                }
            };
            if (GWT_VERSION === GWTVersionEnum.Two) {
                singlePaymentPayload.gwt = {
                    gwtVersion: GWT_VERSION,
                    recid: clientDetail && clientDetail.id
                };
            }
            debugMode && console.info("createSinglePayment", singlePaymentPayload);
            await createSinglePayment(singlePaymentPayload);
        } else {
            const firstPaymentPayload: any = {
                ...commonPayload,
                type: paymentFrequency === "monthly" ? "MonthlyEnrollment" : "AnnualEnrollment",
            };
            isDebug() && console.info("createFirstPayment", firstPaymentPayload);
            await createFirstPayment(firstPaymentPayload);
        }

        // refresh policy details
        await refetchPolicy();
        refreshList && await refreshList(true);
        setOpenPopup(false)

        submitting(false);
    }

    const validationSchema = Yup.object({
        [fieldsName.trancheNo]: Yup.string().required('The tranche number is required').nullable()
    })

    return (
            <Formik
                    initialValues={initialValues}
                    enableReinitialize={true}
                    validationSchema={validationSchema}
                    validateOnChange={true}
                    onSubmit={submitFormik}
            >
                {
                    ({errors, handleSubmit, values}: any) => {
                        const onQCFormSubmit = (e: any) => {
                            e.preventDefault();
                            handleSubmit(e);
                        };
                        return (
                                <form id={"QCEpayForm"} onSubmit={onQCFormSubmit}>
                                    <Initializer actionPayload={actionPayload}/>
                                    <p>
                                        The following eACh payment has been initiated by {clientName}. Review the
                                        payment details and Reject or Accept
                                    </p>
                                    <FormFields/>
                                    {children}
                                    {
                                            isDebug() &&
                                            <pre>{JSON.stringify({errors, values}, null, 2)}</pre>
                                    }
                                </form>
                        );
                    }
                }
            </Formik>
    )
};


const Initializer = ({actionPayload}: {actionPayload?: any}) => {
    const {policy}: any = usePolicyContext();
    const {client, clientPolicy} = useContext(QcPopUpContext);
    const paymentMethod: undefined | paymentMethodsType = (policy && policy.paymentMethods && policy.paymentMethods.length > 0) && policy.paymentMethods[0]
    const {setValues} = useFormikContext<any>()
    const paymentFrequency: string = clientPolicy && clientPolicy.paymentFrequency || "annual";
    const tranche = clientPolicy && clientPolicy.tranche || {};
    React.useEffect(() => {
        if (paymentMethod) {
            console.info({actionPayload})
            const timeTable = paymentFrequency === "annual" ? policy.paymentTimetableAnnual : policy.paymentTimetableMonthly;
            const newPaymentAmount = timeTable.nextPayment.amount && (timeTable.nextPayment.amount[0].amount + timeTable.nextPayment.amount[1].amount + timeTable.nextPayment.amount[2].amount);
            setValues({
                [fieldsName.accountNumber]: paymentMethod.maskedAccountNumber,
                [fieldsName.paymentMethod]: "ePay",
                [fieldsName.bankName]: paymentMethod.bankName,
                [fieldsName.amountPaid]: !!actionPayload ? getPaymentAmount(actionPayload.amount) : newPaymentAmount,
                [fieldsName.trancheNo]: tranche && tranche.trancheNumber + "",
                [fieldsName.bankAccHolder]: paymentMethod.clientName || "",
                [fieldsName.paymentMethodId]: policy.paymentMethods && policy.paymentMethods[0].recordKey || "",
                [fieldsName.checkFront]: Array.isArray(paymentMethod.files) && paymentMethod.files.length > 0 && (paymentMethod.files.find((name) => name.includes("Frontofcheck")) || policy.paymentMethods[0].files[0]),
                // [fieldsName.checkBack] : policy.paymentMethods && policy.paymentMethods[0].files.find((name)=> name.includes("Backofcheck")) || policy.paymentMethods[0].files[0],
                // [fieldsName.] = timeTable && timeTable.recurringPayments && moment(timeTable.recurringPayments[0].date).format("MM/DD/YYYY") || "";
            })
        }
    }, [paymentMethod, client])

    return <></>
}



